
import React, { useState } from "react";
import { PlusCircle, Filter, ArrowUpDown } from "lucide-react";
import { episodes, guests } from "@/data/mockData";
import ShowList from "@/components/ShowList";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const Shows = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState("all");

  // Filter episodes based on search and active tab
  const filteredEpisodes = episodes.filter((episode) => {
    const guest = guests.find((g) => g.id === episode.guestId);
    const matchesSearch = 
      episode.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      guest?.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      episode.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      episode.showNotes?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      episode.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));
    
    if (activeTab === "all") return matchesSearch;
    return episode.status === activeTab && matchesSearch;
  });

  return (
    <div className="container mx-auto py-8 px-4 md:px-6 animate-fade-in">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
        <div>
          <h1 className="text-3xl font-bold text-metsci-primary">Episodes</h1>
          <p className="text-muted-foreground mt-1">Manage your podcast episodes</p>
        </div>
        <Button className="bg-metsci-primary hover:bg-metsci-red text-metsci-white">
          <PlusCircle className="mr-2 h-4 w-4" />
          Create New Episode
        </Button>
      </div>
      
      <div className="flex flex-col md:flex-row gap-4 mb-6 justify-between">
        <div className="relative w-full md:w-96">
          <Input
            className="pl-10 border-metsci-blue/20 focus-visible:ring-metsci-blue"
            placeholder="Search episodes..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
        </div>
        
        <div className="flex space-x-2">
          <Button variant="outline" size="sm" className="text-metsci-black border-metsci-blue/20">
            <Filter className="mr-2 h-4 w-4" />
            Filter
          </Button>
          <Button variant="outline" size="sm" className="text-metsci-black border-metsci-blue/20">
            <ArrowUpDown className="mr-2 h-4 w-4" />
            Sort
          </Button>
        </div>
      </div>
      
      <Tabs defaultValue="all" className="mb-6" onValueChange={setActiveTab}>
        <TabsList className="bg-metsci-white border border-metsci-blue/10">
          <TabsTrigger 
            value="all"
            className="data-[state=active]:bg-metsci-blue data-[state=active]:text-white"
          >
            All Shows
          </TabsTrigger>
          <TabsTrigger 
            value="published"
            className="data-[state=active]:bg-metsci-blue data-[state=active]:text-white"
          >
            Published
          </TabsTrigger>
          <TabsTrigger 
            value="scheduled"
            className="data-[state=active]:bg-metsci-blue data-[state=active]:text-white"
          >
            Scheduled
          </TabsTrigger>
          <TabsTrigger 
            value="completed"
            className="data-[state=active]:bg-metsci-blue data-[state=active]:text-white"
          >
            Completed
          </TabsTrigger>
          <TabsTrigger 
            value="pending"
            className="data-[state=active]:bg-metsci-blue data-[state=active]:text-white"
          >
            Pending
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="all" className="mt-4">
          <ShowList episodes={filteredEpisodes} guests={guests} />
        </TabsContent>
        <TabsContent value="published" className="mt-4">
          <ShowList episodes={filteredEpisodes} guests={guests} />
        </TabsContent>
        <TabsContent value="scheduled" className="mt-4">
          <ShowList episodes={filteredEpisodes} guests={guests} />
        </TabsContent>
        <TabsContent value="completed" className="mt-4">
          <ShowList episodes={filteredEpisodes} guests={guests} />
        </TabsContent>
        <TabsContent value="pending" className="mt-4">
          <ShowList episodes={filteredEpisodes} guests={guests} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Shows;
